import { useTranslation } from "react-i18next";
import MonthPicker from "features/datepicker/month-picker";
import Notice from "components/tw/notice";
import { formatPeriodMonth, monthsInclusive } from "./billing-period";

type Props = {
  /** `YYYY-MM`, or "" to take the default. */
  value: string;
  onChange: (value: string) => void;
  /** The start as loaded, `YYYY-MM` — an unchanged start warns about nothing. */
  loadedValue: string;
  /** What the server stores when the field is left empty, `YYYY-MM`. */
  defaultMonth: string;
  /** The latest period the chosen mode bills today, `YYYY-MM`; "" when unknown. */
  capMonth: string;
  /** True when the caller may only view this field, not change it. */
  readOnly?: boolean;
};

/**
 * "Bill from" for a recurring order. The cron catches up every unbilled month
 * from the start, so a start earlier than the default is back-billing: say how
 * many invoices that drafts on the next run before the user saves it.
 */
export default function BillingStartField({
  value,
  onChange,
  loadedValue,
  defaultMonth,
  capMonth,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();

  const backfills =
    !readOnly &&
    !!value &&
    value !== loadedValue &&
    !!defaultMonth &&
    value < defaultMonth;
  const count = backfills && capMonth ? monthsInclusive(value, capMonth) : 0;

  return (
    <div className="space-y-2">
      <MonthPicker
        label={t("invoice_billing_start")}
        value={value}
        placeholder={defaultMonth}
        onChange={onChange}
        helpText={t("invoice_billing_start_help")}
        readOnly={readOnly}
      />
      {backfills ? (
        <Notice tone="warning">
          <p>
            {count
              ? t("invoice_billing_start_backfill", {
                  n: count,
                  from: formatPeriodMonth(`${value}-01`),
                  to: formatPeriodMonth(`${capMonth}-01`),
                })
              : t("invoice_billing_start_backfill_open", {
                  from: formatPeriodMonth(`${value}-01`),
                })}
          </p>
        </Notice>
      ) : null}
    </div>
  );
}
